<template>
  <!-- Sticky banner above the message list when the thread has a pinned
       message. Click jumps the scroll to the source message; an X
       button unpins (visible only to users with pin permission). -->
  <div
    v-if="pinnedMessageId"
    class="shrink-0 flex items-center gap-2 px-3 py-1.5 bg-primary/[0.06] border-b border-primary/15 cursor-pointer hover:bg-primary/[0.09] transition-colors"
    :title="$t('chat.pinned.jump_to')"
    @click="$emit('jump', pinnedMessageId)"
  >
    <el-icon size="12" class="text-primary shrink-0"><Top /></el-icon>
    <span class="text-[10px] uppercase tracking-widest font-bold text-primary shrink-0">
      {{ $t('chat.pinned.label') }}
    </span>
    <p class="text-xs text-text-muted truncate flex-1">
      {{ snippet }}
    </p>
    <button
      v-if="canUnpin"
      type="button"
      class="shrink-0 p-1 rounded text-text-muted hover:text-rose-300 hover:bg-rose-400/10"
      :title="$t('chat.pinned.unpin')"
      :aria-label="$t('chat.pinned.unpin')"
      :disabled="unpinning"
      @click.stop="onUnpin"
    >
      <el-icon size="12"><Close /></el-icon>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Top, Close } from '@element-plus/icons-vue'

const props = defineProps<{
  threadId: string
  pinnedMessageId: string | null
  /** Snippet of the pinned message body (for the banner text). */
  snippet: string
  canUnpin?: boolean
}>()
const emit = defineEmits<{
  jump: [messageId: string]
  unpinned: []
}>()

const unpinning = ref(false)

async function onUnpin() {
  if (unpinning.value) return
  unpinning.value = true
  try {
    await $fetch(`/api/chat/threads/${props.threadId}/pin`, { method: 'DELETE' })
    emit('unpinned')
  } catch {
    // WS frame is the source of truth — failure is silent
  } finally {
    unpinning.value = false
  }
}
</script>
